import { useState, useCallback, useEffect } from 'react';
import { Form, Button, Card, Alert } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import type { Banner } from '../../../../apps/hooks/banner/useBanner';
import { useBanner } from '../../../../apps/hooks/banner/useBanner';
import { formatDateToYYYYMMDD } from '../../../../utils/dateUtils';
import type { BannerUploadData, BannerFormData, BannerService } from './components';
import { DeviceType, BannerCard, processFile as processFileUtil } from './components';
import {
  BANNER_MOBILE_IMAGE_MAX_BYTES,
  BANNER_PC_IMAGE_MAX_BYTES,
  BANNER_VIDEO_MAX_BYTES,
  formatKb,
  formatMb,
} from './components/FileProcessingUtils';
import DropzoneContent from './components/DropzoneContent';

export default function BannerUploadPage() {
  const banner = useBanner();

  // Calculate a date 4 months in the future for the default toDate
  const getDefaultToDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 4);
    return date;
  };

  const [uploadedFiles, setUploadedFiles] = useState<BannerUploadData[]>([]);
  const dateRange = {
    fromDate: new Date(),
    toDate: getDefaultToDate(),
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [processingQueue, setProcessingQueue] = useState<File[]>([]);
  const [processedCount, setProcessedCount] = useState(0);

  // Process the upload queue one by one
  useEffect(() => {
    const processNextInQueue = async () => {
      if (processingQueue.length > 0 && !isUploading) {
        setIsUploading(true);

        try {
          const nextFile = processingQueue[0];
          if (nextFile) {
            const remainingQueue = processingQueue.slice(1);
            setProcessingQueue(remainingQueue);

            await processFile(nextFile);
            setProcessedCount((prev) => prev + 1);
          }
        } catch (err) {
          console.error('Error processing file:', err);
          setError(err instanceof Error ? err.message : 'An error occurred while processing a file');
        } finally {
          setIsUploading(false);
        }
      }
    };

    processNextInQueue();
  }, [processingQueue, isUploading]);

  // Main file processing function
  const processFile = async (file: File) => {
    await processFileUtil(file, banner as BannerService, uploadedFiles, setUploadedFiles, dateRange);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setError(null);

    if (acceptedFiles.length === 0) {
      return;
    }

    // Add all files to the processing queue
    setProcessingQueue((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/webp': ['.webp'],
      'video/mp4': ['.mp4'],
    },
    multiple: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (uploadedFiles.length === 0) {
      setError('Please upload at least one image');
      return;
    }

    // Validate all files have required fields (only title is required)
    const missingFields = uploadedFiles.find((file) => !file.title);

    if (missingFields) {
      setError('Please fill in the title for each banner');
      return;
    }

    // Validate each banner has at least one image
    const missingImages = uploadedFiles.find((file) => !file.imageUrl && !file.tabletImageUrl && !file.mobileImageUrl);

    if (missingImages) {
      setError('Each banner must have at least one image (desktop, tablet, or mobile)');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create individual banners with their own configurations
      const banners = uploadedFiles.map((file) => ({
        imageUrl: file.imageUrl || '',
        mobileImageUrl: file.mobileImageUrl,
        tabletImageUrl: file.tabletImageUrl,
        title: file.title,
        description: file.description || '',
        linkUrl: file.linkUrl || '',
        location: file.location || '/',
        // Format dates as YYYY-MM-DD strings for the API
        fromDate: formatDateToYYYYMMDD(file.fromDate),
        toDate: formatDateToYYYYMMDD(file.toDate),
      }));

      // Server will assign bannerId and date fields
      await banner.createBanners(banners as Banner[]);

      setSuccess(`${uploadedFiles.length} banner(s) created successfully`);
      setUploadedFiles([]);
      setProcessedCount(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setUploadedFiles([]);
    setError(null);
    setSuccess(null);
  };

  const removeUploadedFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeUploadingFile = (fileName: string) => {
    banner.removeUploadingFile(fileName);
  };

  const removeImage = (index: number, type: DeviceType) => {
    setUploadedFiles((prev) => {
      const updated = [...prev];
      const bannerCopy = { ...updated[index] };

      // Ensure imageFiles exists
      if (!bannerCopy.imageFiles) {
        bannerCopy.imageFiles = {};
      }

      // Remove the image URL and file reference
      if (type === DeviceType.DESKTOP) {
        bannerCopy.imageUrl = ''; // Use empty string instead of undefined
        if (bannerCopy.imageFiles) {
          bannerCopy.imageFiles[DeviceType.DESKTOP] = undefined;
        }
      } else if (type === DeviceType.TABLET) {
        bannerCopy.tabletImageUrl = ''; // Use empty string instead of undefined
        if (bannerCopy.imageFiles) {
          bannerCopy.imageFiles[DeviceType.TABLET] = undefined;
        }
      } else if (type === DeviceType.MOBILE) {
        bannerCopy.mobileImageUrl = ''; // Use empty string instead of undefined
        if (bannerCopy.imageFiles) {
          bannerCopy.imageFiles[DeviceType.MOBILE] = undefined;
        }
      }

      updated[index] = bannerCopy as BannerUploadData; // Force the type
      return updated;
    });
  };

  const updateFileField = (index: number, field: keyof BannerFormData, value: any) => {
    setUploadedFiles((prev) => prev.map((file, i) => (i === index ? { ...file, [field]: value } : file)));
  };

  // Added navigate handler
  const handleNavigateToBannerList = () => {
    window.location.href = '/site-management/banner/list';
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Upload Banner Images/Videos</h2>
        <Button variant="primary" onClick={handleNavigateToBannerList}>
          Go to Banner List
        </Button>
      </div>

      <div className="text-muted mb-4">
        <p>Upload and manage banner images for your campaign. This page allows you to:</p>
        <ul>
          <li>Upload multiple banner images for desktop, tablet, and mobile devices</li>
          <li>Images are automatically grouped based on their filenames and dimensions</li>
          <li>Customize title, description and link for each banner</li>
        </ul>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-4">
          {success}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Body>
            <h3 className="mb-3">Upload Images/Videos</h3>
            <div className="alert alert-info mb-3">
              <h5>Banner Image Format</h5>
              <p className="mb-2">Include dimensions in filename to specify device type</p>
              <div className="mb-1">
                <strong>Example:</strong> For the same banner, name your files:
              </div>
              <ul className="mb-2">
                <li>
                  <code>BannerName_1280x600.webp</code> → Desktop (max {formatKb(BANNER_PC_IMAGE_MAX_BYTES)})
                </li>
                <li>
                  <code>BannerName_1170x400.webp</code> → Tablet (max {formatKb(BANNER_PC_IMAGE_MAX_BYTES)})
                </li>
                <li>
                  <code>BannerName_360x390.webp</code> → Mobile (max {formatKb(BANNER_MOBILE_IMAGE_MAX_BYTES)})
                </li>
              </ul>
              <div className="mb-0">
                <strong>File size limits:</strong> PC banner image ≤ {formatKb(BANNER_PC_IMAGE_MAX_BYTES)} · Mobile
                banner image ≤ {formatKb(BANNER_MOBILE_IMAGE_MAX_BYTES)} · MP4 banner video ≤{' '}
                {formatMb(BANNER_VIDEO_MAX_BYTES)}
              </div>
            </div>
            <p>Images/Videos are immediately uploaded to S3 when you drop them.</p>
            <div
              {...getRootProps()}
              className={`border border-2 border-secondary rounded p-4 text-center ${isDragActive ? 'bg-light' : 'bg-secondary bg-opacity-10'}`}
              style={{ cursor: 'pointer' }}
            >
              <input {...getInputProps()} />
              {isDragActive ? (
                <DropzoneContent title="Drop the image/video files here..." />
              ) : (
                <DropzoneContent title="Drag and drop image/video files here or click to select files" />
              )}
            </div>

            {processingQueue.length > 0 && (
              <div className="alert alert-info mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    Processing {processedCount} of {processingQueue.length + processedCount} images...
                  </span>
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
                <div className="progress mt-2" style={{ height: '5px' }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${(processedCount / (processingQueue.length + processedCount)) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {Object.values(banner.uploadingFiles).length > 0 && (
              <div className="mt-4">
                <h6>Uploading Files:</h6>
                {Object.values(banner.uploadingFiles).map((file) => (
                  <div key={file.name} className="mb-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <small>{file.name}</small>
                      <div>
                        <small className="me-2">{file.progress}%</small>
                        <Button size="sm" variant="outline-danger" onClick={() => removeUploadingFile(file.name)}>
                          Remove
                        </Button>
                      </div>
                    </div>
                    <div className="progress" style={{ height: '5px' }}>
                      <div
                        className={`progress-bar ${file.error ? 'bg-danger' : ''}`}
                        role="progressbar"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                    {file.error && <small className="text-danger">{file.error}</small>}
                  </div>
                ))}
              </div>
            )}

            {uploadedFiles.length > 0 && (
              <div className="mt-4">
                <h3>Uploaded Banners ({uploadedFiles.length})</h3>
                {uploadedFiles.map((file, index) => (
                  <BannerCard
                    key={index}
                    index={index}
                    file={file}
                    removeUploadedFile={removeUploadedFile}
                    removeImage={removeImage}
                    updateFileField={updateFileField}
                    banner={banner as BannerService}
                    isUploading={isUploading}
                    setIsUploading={setIsUploading}
                    setError={setError}
                    setUploadedFiles={setUploadedFiles}
                  />
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        <div className="d-flex gap-2 justify-content-end">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isSubmitting || isUploading || uploadedFiles.length === 0}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || isUploading || processingQueue.length > 0 || uploadedFiles.length === 0}
          >
            {isSubmitting ? 'Saving...' : `Register ${uploadedFiles.length} Banner(s)`}
          </Button>
        </div>
      </Form>
    </div>
  );
}
